import { Form, Text, View, XStack, YStack } from "tamagui";
import { useCallback, useEffect, useState } from "react";
import { colors } from "@styles/colors";
import MapPicker from "./MapPicker";
import FormItem from "@components/form/FormItem";
import { FormProvider, useWatch, UseFormReturn } from "react-hook-form";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import { isAreaInCityValid, combinedAddress, addressToGeocode, haversineDistance } from "./func/addressTool";
import { handleAPIGetCity, handleAPIGetTown, handleAPIGeocodeGetAddress, handleAPIAddressComponents, type AddressComponent } from "./API/handleAddressAPI";
import { cleanKeyword } from "./func/addressTextTool";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import toast from "react-hot-toast";
import { toToastMessage } from "@utils/toastMessage";
import SimpleAlert from "@components/display/SimplePopAlert";
import { getLocalAreaOptionsForDev, getLocalCityOptionsForDev, isLocalAddressDevBypassEnabled } from "./func/devAddressFallback";
import RadiusCard from "@components/layout/RadiusCard";

/** 補「樓」尾字；已有則不加，空值傳 undefined */
const normalizeFloor = (val?: string): string | undefined => {
  if (!val?.trim()) return undefined;
  return val.trim().endsWith("樓") ? val.trim() : val.trim() + "樓";
};

/** 補「室」尾字；已有則不加，空值傳 undefined */
const normalizeRoom = (val?: string): string | undefined => {
  if (!val?.trim()) return undefined;
  return val.trim().endsWith("室") ? val.trim() : val.trim() + "室";
};

interface AddressFormProps {
  methods: UseFormReturn<any>;
  onSubmit: (data: any) => void | Promise<void>;
  /**
   * 編輯現有地址時由外部傳入（含 latitude/longitude）；
   * 有值時啟用 A/B/mixed 路徑送出驗證，無值時走新增地址流程。
   */
  initialValues?: {
    latitude?: number | null;
    longitude?: number | null;
    city?: string;
    area?: string;
    road?: string;
    streetNumber?: string;
  };
  /** 呼叫端 API 失敗時由外部傳入（例如「無法配送此區域」），顯示在表單底部 */
  externalError?: string | null;
}

const AddressForm = ({ methods, onSubmit, initialValues, externalError }: AddressFormProps) => {
  const { lineAccessToken } = useLineAuth();
  const { setAutoCompleteAddress } = useAddressStore();
  const {
    control,
    setValue,
    formState: { errors },
  } = methods;

  const allFields = methods.watch();
  const watchCity = useWatch({ control, name: "city" });
  const watchArea = useWatch({ control, name: "area" });

  const [cityOptions, setCityOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  /** 儲存／經緯度驗證錯誤，直接顯示在表單上（紅字） */
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** 地址與地圖座標不符時的待送出資料（等使用者確認後才 submit） */
  const [pendingSubmitData, setPendingSubmitData] = useState<any>(null);
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  /** 使用者是否曾手動拖動地圖（A 路徑依據） */
  const [hasMapMoved, setHasMapMoved] = useState(false);
  /** 使用者是否曾修改 city/area/road/streetNumber（B 路徑依據） */
  const [hasFormFieldChanged, setHasFormFieldChanged] = useState(false);
  /** 是否為編輯現有地址模式（有 initialValues 即視為是） */
  const isEditMode = !!initialValues;
  /** MapPicker 目前的超距警告文字（非 null = 上次拖動超過 500m，尚未解除） */
  const [mapDragWarning, setMapDragWarning] = useState<string | null>(null);
  /** MapPicker 是否正在定位（依地址取座標／GPS）→ 期間卡控送出，避免用到尚未更新的座標 */
  const [isMapGeocoding, setIsMapGeocoding] = useState(false);
  const debugEnabled = process.env.NODE_ENV !== "production";
  const isLocalDevBypass = isLocalAddressDevBypassEnabled();
  const debugLog = (...args: unknown[]) => {
    if (!debugEnabled) return;
    console.log("[AddressDebug][AddressForm]", ...args);
  };
  const geocodeAddressForSubmit = async (address: string) => {
    if (!address) return null;

    debugLog("submit-geocode-start", { address });
    let geoRes = await addressToGeocode(address);
    if (geoRes?.state === "success") {
      const nextLat = Number(geoRes.lat);
      const nextLng = Number(geoRes.lng);
      if (!Number.isNaN(nextLat) && !Number.isNaN(nextLng)) {
        debugLog("submit-geocode-success", { address, nextLat, nextLng });
        return { lat: nextLat, lng: nextLng };
      }
    }

    debugLog("submit-geocode-failed", { address, result: geoRes });
    return null;
  };

  //-- 處理縣市選項
  const getCityOptions = useCallback(async () => {
    const res = await handleAPIGetCity(lineAccessToken || "");
    if (res?.state === "success") {
      const formatList = res?.list.map((item: { city: string }) => {
        return { label: item?.city, value: item?.city };
      });
      setCityOptions(formatList);
    } else {
      if (isLocalDevBypass) {
        debugLog("fallback-city-options-local-dev");
        setCityOptions(getLocalCityOptionsForDev() as any);
        return;
      }
      toast.error("取得縣市列表失敗");
    }
  }, [lineAccessToken, isLocalDevBypass]);

  //-- 處理區域選項 (根據所選縣市連動)
  const getAreaOptions = useCallback(async () => {
    setAreaOptions([]);
    const res = await handleAPIGetTown(lineAccessToken || "", watchCity);
    if (res?.state === "success") {
      const formatList = res?.list.map((item: { town: string }) => {
        return { label: item?.town, value: item?.town };
      });
      setAreaOptions(formatList);
    } else {
      if (isLocalDevBypass) {
        debugLog("fallback-area-options-local-dev", { city: watchCity });
        setAreaOptions(getLocalAreaOptionsForDev(watchCity || "") as any);
        return;
      }
      toast.error("取得鄉鎮市區列表失敗");
    }
  }, [lineAccessToken, watchCity, isLocalDevBypass]);

  useEffect(() => {
    getCityOptions();
  }, [getCityOptions]);

  useEffect(() => {
    if (watchCity) {
      getAreaOptions();
    }
  }, [watchCity, getAreaOptions]);

  useEffect(() => {
    //-- 監聽確認行政區是否隸屬該縣市，為"否"需清空行政區 area
    if (watchCity && watchArea && !isAreaInCityValid(watchCity, watchArea)) {
      setValue("area", "");
      setValue("latitude", null);
      setValue("longitude", null);
      setAutoCompleteAddress(null);
    }
  }, [watchCity, watchArea]);

  //-- 選擇/輸入變更時：選縣市清空以下全部；選鄉鎮市區清空以下；改路名或門牌則清空經緯度讓地圖重新 geocode
  // 經緯度欄位 schema 為 z.number().optional().nullable()，清空時須設 null 不可設 ""，否則 Zod 會報 "expected number, received string"
  const clearBelowCity = () => {
    setValue("area", "");
    setValue("road", "");
    setValue("streetNumber", "");
    setValue("floor", "");
    setValue("room", "");
    setValue("otherAddress", "");
    setValue("latitude", null);
    setValue("longitude", null);
    setAutoCompleteAddress(null);
  };
  const clearBelowArea = () => {
    setValue("road", "");
    setValue("streetNumber", "");
    setValue("floor", "");
    setValue("room", "");
    setValue("otherAddress", "");
    setValue("latitude", null);
    setValue("longitude", null);
    setAutoCompleteAddress(null);
  };
  const clearLatLngForMapUpdate = () => {
    setValue("latitude", null);
    setValue("longitude", null);
    setAutoCompleteAddress(null);
  };
  const handleAddressFieldChange = (id: string) => {
    if (id === "city") clearBelowCity();
    else if (id === "area") clearBelowArea();
    else if (id === "road" || id === "streetNumber") clearLatLngForMapUpdate();
    if (["city", "area", "road", "streetNumber"].includes(id)) {
      setHasFormFieldChanged(true);
    }
  };

  //-- 送出前統一驗證：依 isEditMode / hasMapMoved / hasFormFieldChanged 分流 A/B/mixed/none
  const handleSubmitWithGeocodeValidation = async (data: any) => {
    setSubmitError(null);
    const lat = data?.latitude != null && data?.latitude !== "" ? Number(data.latitude) : null;
    const lng = data?.longitude != null && data?.longitude !== "" ? Number(data.longitude) : null;
    debugLog("submit-clicked", {
      isEditMode,
      lat,
      lng,
      data,
      mapDragWarning,
      hasMapMoved,
      hasFormFieldChanged,
    });

    // 樓/室 尾字正規化（所有路徑統一處理）
    const normFloor = normalizeFloor(data.floor);
    const normRoom = normalizeRoom(data.room);
    if (normFloor !== undefined) setValue("floor", normFloor);
    if (normRoom !== undefined) setValue("room", normRoom);
    const floorVal = normFloor ?? data.floor;
    const roomVal = normRoom ?? data.room;

    // 若地圖警告尚未解除（上次拖動超過 500m），直接擋送出（新增/修改皆適用）
    if (mapDragWarning !== null) {
      setSubmitError("此位置與原地址相距超過 500 公尺，請調整後再儲存。");
      return;
    }

    // ── 新增地址流程（非編輯模式）：保留原本邏輯 ──────────────────────────
    if (!isEditMode) {
      let finalLat = lat;
      let finalLng = lng;

      if (finalLat == null || finalLng == null || Number.isNaN(finalLat) || Number.isNaN(finalLng)) {
        const addressStr = combinedAddress({
          city: data.city || "",
          area: data.area || "",
          road: data.road || "",
          streetNumber: data.streetNumber || "",
        });
        const geoPoint = await geocodeAddressForSubmit(addressStr);
        if (geoPoint) {
          finalLat = geoPoint.lat;
          finalLng = geoPoint.lng;
          setValue("latitude", finalLat as any);
          setValue("longitude", finalLng as any);
        }
      }

      if (finalLat == null || finalLng == null || Number.isNaN(finalLat) || Number.isNaN(finalLng)) {
        debugLog("submit-blocked-missing-latlng", {
          address: {
            city: data.city,
            area: data.area,
            road: data.road,
            streetNumber: data.streetNumber,
          },
        });
        setSubmitError("請確認地址已正確定位（可移動地圖或等待地址解析） ");
        return;
      }

      const geocodeRes = await handleAPIGeocodeGetAddress({ lat: finalLat, lng: finalLng }, lineAccessToken || "");
      if (geocodeRes != null && geocodeRes.state === "success" && Array.isArray(geocodeRes.possibleList) && geocodeRes.possibleList.length === 0) {
        setSubmitError("經緯度無法對應到有效地址，請重新選擇位置或修正地址");
        return;
      }
      const address = combinedAddress({ city: data.city || "", area: data.area || "", road: data.road || "", streetNumber: data.streetNumber || "" });
      if (address) {
        const addrGeoRes = await addressToGeocode(address);
        if (addrGeoRes?.state === "success") {
          const addrLat = Number(addrGeoRes.lat);
          const addrLng = Number(addrGeoRes.lng);
          if (!Number.isNaN(addrLat) && !Number.isNaN(addrLng) && haversineDistance(finalLat, finalLng, addrLat, addrLng) > 1000) {
            setPendingSubmitData({ ...data, latitude: finalLat, longitude: finalLng, floor: floorVal, room: roomVal });
            setMismatchDialogOpen(true);
            return;
          }
        }
      }
      setSubmitError(null);
      debugLog("submit-success-new-address", {
        finalLat,
        finalLng,
      });
      onSubmit({ ...data, latitude: finalLat, longitude: finalLng, floor: floorVal, room: roomVal });
      return;
    }

    // ── 編輯模式：A/B/mixed/none 分流 ────────────────────────────────────
    const editPath =
      hasMapMoved && !hasFormFieldChanged ? "A"
      : !hasMapMoved && hasFormFieldChanged ? "B"
      : hasMapMoved && hasFormFieldChanged ? "mixed"
      : "none";

    // A 路徑：只有地圖移動
    if (editPath === "A") {
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
        setSubmitError("請確認地址已正確定位");
        return;
      }
      // 送出前再做一次 500m hard check（MapPicker 拖動時已限制，此為雙重保障）
      const origLat = initialValues?.latitude != null ? Number(initialValues.latitude) : null;
      const origLng = initialValues?.longitude != null ? Number(initialValues.longitude) : null;
      if (origLat != null && origLng != null && !Number.isNaN(origLat) && !Number.isNaN(origLng)) {
        if (haversineDistance(origLat, origLng, lat, lng) > 500) {
          setSubmitError("此位置與原地址相距超過 500 公尺，請調整後再儲存。");
          return;
        }
      }
      // Geocode gate：失敗或空結果均擋送出
      const geocodeRes = await handleAPIGeocodeGetAddress({ lat, lng }, lineAccessToken || "");
      if (!geocodeRes || geocodeRes.state !== "success" || !Array.isArray(geocodeRes.possibleList) || geocodeRes.possibleList.length === 0) {
        setSubmitError("無法取得座標對應地址，請稍後再試或改用手動輸入。");
        return;
      }
      setSubmitError(null);
      onSubmit({ ...data, floor: floorVal, room: roomVal });
      return;
    }

    // B 路徑（只改表單）/ mixed（兩者都改）：必須做正規化
    if (editPath === "B" || editPath === "mixed") {
      const addressStr = combinedAddress({ city: data.city || "", area: data.area || "", road: data.road || "", streetNumber: data.streetNumber || "" });
      const normRes = await handleAPIAddressComponents([addressStr], lineAccessToken || "");
      if (normRes.state !== "success" || !Array.isArray(normRes.data) || !(normRes.data as AddressComponent[])[0]) {
        setSubmitError("地址解析失敗，請確認縣市/區域/路名/門牌號後重試。");
        return;
      }
      const normItem = (normRes.data as AddressComponent[])[0];
      const normCity = normItem.administrative_area_level_1 || data.city;
      const normArea = normItem.administrative_area_level_2 || data.area;
      const normRoad = normItem.route || data.road;
      const normStreetNumber = cleanKeyword(normItem.street_number || data.streetNumber || "", "號");
      setValue("city", normCity);
      setValue("area", normArea);
      setValue("road", normRoad);
      setValue("streetNumber", normStreetNumber);

      // B 路徑取座標（mixed 路徑以地圖當前座標為主，不重新 geocode）
      let finalLat = lat;
      let finalLng = lng;
      if (editPath === "B") {
        const normAddrStr = combinedAddress({ city: normCity, area: normArea, road: normRoad, streetNumber: normStreetNumber });
        const geoRes = await addressToGeocode(normAddrStr);
        if (geoRes?.state === "success" && !Number.isNaN(Number(geoRes.lat)) && !Number.isNaN(Number(geoRes.lng))) {
          finalLat = Number(geoRes.lat);
          finalLng = Number(geoRes.lng);
          setValue("latitude", finalLat as any);
          setValue("longitude", finalLng as any);
        } else if (finalLat == null || finalLng == null) {
          setSubmitError("無法取得此地址的座標，請稍後再試。");
          return;
        }
      }
      setSubmitError(null);
      onSubmit({ ...data, city: normCity, area: normArea, road: normRoad, streetNumber: normStreetNumber, latitude: finalLat, longitude: finalLng, floor: floorVal, room: roomVal });
      return;
    }

    // none：無任何變更，直接送出（正規化僅 floor/room）
    setSubmitError(null);
    onSubmit({ ...data, floor: floorVal, room: roomVal });
  };

  /** 表單驗證失敗時的第一筆錯誤訊息（顯示在表單上紅字） */
  const firstValidationError =
    Object.keys(errors).length > 0
      ? toToastMessage((Object.values(errors)[0] as { message?: string })?.message, "請檢查並填寫必填欄位")
      : null;
  const displayError = externalError ?? submitError ?? firstValidationError;

  return (
    <>
      <SimpleAlert
        isOpen={mismatchDialogOpen}
        onClose={() => setMismatchDialogOpen(false)}
        title={{ str: "地址與地圖位置不符" }}
        content={{ str: "您輸入的地址與地圖標記位置相差超過 1 公里，請確認兩者是否一致。" }}
        rightButton={{
          str: "重新修改",
          onPress: () => setMismatchDialogOpen(false),
        }}
        leftButton={{
          str: "確認送出",
          onPress: () => {
            setMismatchDialogOpen(false);
            if (pendingSubmitData) onSubmit(pendingSubmitData);
          },
        }}
      />
      <YStack p={"$5"}>
        <FormProvider {...methods}>
          <MapPicker
            formFields={allFields}
            setValue={setValue}
            moveHintText="移動來修改地址"
            onMapMoved={() => setHasMapMoved(true)}
            onDragWarningChange={setMapDragWarning}
            onGeocodingChange={setIsMapGeocoding}
          />
          <YStack>
            {/* 資訊提示條 */}
            <YStack px="$0" py="$4">
              <XStack w="100%" borderRadius="$4" borderWidth="$1" borderColor="#BEE5EB" bg="#D1ECF1" px="$3" py="$3" gap="$2" alignItems="flex-start">
                <View mt="$0.5" width={20} height={20} borderRadius={9999} bg="#0C5460" alignItems="center" justifyContent="center">
                  <Text fontSize={12} fontWeight="700" color="#FFF">
                    i
                  </Text>
                </View>
                <Text fontSize={14} fontWeight="600" color="#0C5460" lineHeight={20} flex={1}>
                  配送人員將以圖釘位置為主，您可在「備註」補充門牌號等詳細地址資訊
                </Text>
              </XStack>
            </YStack>
            <Form gap="$4">
              <RadiusCard>
                <FormItem
                  itemType="select"
                  required={true}
                  data={{
                    id: "city",
                    label: "縣市",
                    placeholder: "",
                    value: "",
                    otherInfo: "",
                    options: cityOptions,
                  }}
                  onAfterChange={handleAddressFieldChange}
                />
                <FormItem
                  itemType="select"
                  required={true}
                  disabled={!watchCity}
                  data={{
                    id: "area",
                    label: "鄉鎮市區",
                    placeholder: watchCity ? "請選擇區域" : "請先選擇縣市",
                    value: "",
                    otherInfo: "",
                    options: areaOptions,
                  }}
                  onAfterChange={handleAddressFieldChange}
                />
                <FormItem
                  itemType="input"
                  required={true}
                  data={{
                    id: "road",
                    label: "路名/街名",
                    placeholder: "",
                    value: "",
                    otherInfo: "",
                  }}
                  onAfterChange={handleAddressFieldChange}
                />
                <XStack justifyContent="space-between" w="100%" gap="$3">
                  <YStack f={1}>
                    <FormItem
                      itemType="input"
                      required={true}
                      data={{
                        id: "streetNumber",
                        label: "門牌號",
                        placeholder: "例：1",
                        value: "",
                        otherInfo: "",
                      }}
                      onAfterChange={handleAddressFieldChange}
                    />
                  </YStack>
                  <YStack f={1}>
                    <FormItem
                      itemType="input"
                      required={false}
                      data={{
                        id: "floor",
                        label: "樓層",
                        placeholder: "例：2-1",
                        value: "",
                        otherInfo: "",
                      }}
                    />
                  </YStack>
                </XStack>
                <FormItem
                  itemType="input"
                  required={false}
                  data={{
                    id: "room",
                    label: "室",
                    placeholder: "例：1 或 2-1",
                    value: "",
                    otherInfo: "",
                  }}
                />
              </RadiusCard>
              <RadiusCard>
                <FormItem
                  itemType="textArea"
                  required={false}
                  data={{
                    id: "otherAddress",
                    label: "給配送員的備注 (選填)",
                    placeholder: "例:有大型犬、請按門鈴 3 次、放管理室即可",
                    value: "",
                    otherInfo: "",
                  }}
                />
                <Text color={colors.gray} fontSize={14} mt="$2">
                  幫助配送員順利送達
                </Text>
              </RadiusCard>
              {displayError ? (
                <Text fontSize={14} color="#c00" fontWeight="600">
                  {displayError}
                </Text>
              ) : null}
              <YStack backgroundColor={colors.bg} py={8} style={{ position: "sticky", left: "50px", bottom: "0", width: "100%" }}>
                <XStack
                  className="primary-bg-gradient"
                  borderRadius={20}
                  p={15}
                  alignItems="center"
                  justifyContent="center"
                  opacity={isMapGeocoding ? 0.6 : 1}
                  cursor={isMapGeocoding ? "not-allowed" : "pointer"}
                  onPress={isMapGeocoding ? undefined : methods.handleSubmit(handleSubmitWithGeocodeValidation)}>
                  <XStack gap={10}>
                    <Text color="white" fontSize={16} fontWeight="bold">
                      {isMapGeocoding ? "定位中…" : "確認使用此地址"}
                    </Text>
                  </XStack>
                </XStack>
              </YStack>
            </Form>
          </YStack>
        </FormProvider>
      </YStack>
    </>
  );
};

export default AddressForm;